import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { askItemQuestion } from "../lib/api";

const ASK = {
    modal: "ask-question-modal",
    subject: "ask-question-subject",
    body: "ask-question-body",
    submit: "ask-question-submit",
    cancel: "ask-question-cancel",
};

export const AskQuestionModal = ({ item, open, onOpenChange }) => {
    const navigate = useNavigate();
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open && item) {
            setSubject(`Question about ${item.name}`);
            setBody("");
            setSubmitting(false);
        }
    }, [open, item]);

    if (!item) return null;

    const handleSubmit = async () => {
        if (!body.trim()) {
            toast.error("Add a message");
            return;
        }
        try {
            setSubmitting(true);
            const { question_id } = await askItemQuestion(item.id, {
                subject: subject.trim() || null,
                body: body.trim(),
            });
            toast.success("Question sent", {
                description:
                    "You'll get a reply here on Lendly (and by email). Opening the conversation…",
            });
            onOpenChange(false);
            if (question_id) navigate(`/messages/${question_id}`);
        } catch (err) {
            toast.error("Couldn't send your question", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={ASK.modal}
                className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-[#2e3220]"
            >
                <DialogTitle className="flex items-center gap-2 font-display text-2xl font-bold text-[#2e3220]">
                    <MessageCircle className="h-6 w-6 text-[#737a3d]" strokeWidth={2.5} />
                    Ask a question
                </DialogTitle>
                <DialogDescription className="text-sm text-[#5a6142]">
                    Sending {item.owner_name || "the lender"} a message about{" "}
                    <span className="font-semibold text-[#2e3220]">
                        {item.name}
                    </span>
                    . They&apos;ll get your email so they can reply directly.
                </DialogDescription>

                <div className="mt-5 space-y-4">
                    <div>
                        <label
                            htmlFor="ask-subject"
                            className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                        >
                            Subject
                        </label>
                        <input
                            id="ask-subject"
                            data-testid={ASK.subject}
                            value={subject}
                            onChange={(e) =>
                                setSubject(e.target.value.slice(0, 200))
                            }
                            className="mt-1 w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2.5 text-sm text-[#2e3220] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="ask-body"
                            className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                        >
                            Your question
                        </label>
                        <textarea
                            id="ask-body"
                            data-testid={ASK.body}
                            value={body}
                            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
                            rows={5}
                            placeholder="Hi! Wondering if this fits a 6ft table saw, and whether pickup on Saturday morning works."
                            className="mt-1 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                        <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                            {body.length}/2000
                        </p>
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        data-testid={ASK.cancel}
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="rounded-full border border-[#2e3220]/15 bg-transparent px-5 py-2 text-sm font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid={ASK.submit}
                        onClick={handleSubmit}
                        disabled={submitting || !body.trim()}
                        className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {submitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" strokeWidth={2.5} />
                        )}
                        Send question
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AskQuestionModal;
